import clsx from "clsx";
import type { ComponentProps } from "react";

import type { CityIconsBase } from "@shared/content-types/schemas/common.schema";
import { BrandIcon } from "@shared/ui/brand-icon";
import { Body } from "@shared/ui/typography";

import s from "./line-map.module.scss";

export type LineMapProps = ComponentProps<"div"> & {
  className?: string;
  cities: CityIconsBase[];
};

// CityIconsBase совместим с CityWithTransportIcons
// CityIconsBase.icons имеет тип IconBaseSchema[], который совместим с TransportIcon[]

export const LineMap = (props: LineMapProps) => {
  const { className, cities } = props;

  return (
    <div className={clsx(s.root, className)}>
      <div className={s.cities}>
        {(cities ?? []).map((city, index) => {
          if (!city) return null;
          const cityId = (city as { id?: number }).id ?? `city-${index}`;
          return (
            <div key={cityId} className={s.city}>
              {city?.cityName != null && city.cityName !== "" && (
                <Body
                  size="uppercase-small"
                  weight="semiBold"
                  className={s.cityTitle}
                >
                  {city.cityName}
                </Body>
              )}
              {city?.icons && city?.icons?.length > 0 && (
                <div className={s.cityIcons}>
                  {city.icons?.map((icon, iconIndex) =>
                    icon?.name ? (
                      <BrandIcon
                        key={`${cityId}-${icon.id ?? icon.name ?? iconIndex}`}
                        name={icon.name}
                        size={16}
                        fillColor="violet"
                        strokeColor="milk"
                        className={s.cityIcon}
                      />
                    ) : null,
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <svg
        width="953"
        height="608"
        viewBox="0 0 953 608"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M181.5 74.9677C181.5 74.9677 290.5 21.7356 401.5 63.7359C512.5 105.736 507.5 177.737 507.5 177.737"
          stroke="#F4EADA"
        />
        <path
          d="M182.005 74.9668C182.005 74.9668 302.013 -14.3919 491.484 24.0593C680.954 62.5105 709.5 177.736 709.5 177.736"
          stroke="#F4EADA"
        />
        <path
          d="M182.005 74.9668C182.005 74.9668 302.013 -14.3919 491.484 24.0593C680.954 62.5105 709.5 177.736 709.5 177.736"
          stroke="#F4EADA"
        />
        <path
          d="M181.339 75.2564C181.339 75.2564 224.499 67.2345 260.499 100.735C296.5 134.236 293.5 177.737 293.5 177.737"
          stroke="#F4EADA"
        />
        <path d="M475 202.236L386 202.236" stroke="#F4EADA" />
        <path d="M670 202.023H553" stroke="#F4EADA" />
        <path
          d="M710 527.236C611.736 387.422 508 247.236 508 247.236"
          stroke="#F4EADA"
        />
        <path d="M710 527.236L294 247.236" stroke="#F4EADA" />
        <line
          x1="709.5"
          y1="527.236"
          x2="709.5"
          y2="247.236"
          stroke="#F4EADA"
        />
      </svg>

      <svg
        width="720"
        height="590"
        viewBox="0 0 720 590"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={s.tablet}
      >
        <path
          d="M93.5 91.9677C93.5 91.9677 202.5 38.7356 313.5 80.7359C424.5 122.736 419.5 194.737 419.5 194.737"
          stroke="#F4EADA"
        />
        <path
          d="M94.0051 91.9668C94.0051 91.9668 214.013 2.60815 403.484 41.0593C592.954 79.5105 621.5 194.736 621.5 194.736"
          stroke="#F4EADA"
        />
        <path
          d="M94.0051 91.9668C94.0051 91.9668 214.013 2.60815 403.484 41.0593C592.954 79.5105 621.5 194.736 621.5 194.736"
          stroke="#F4EADA"
        />
        <path
          d="M93.3392 92.2564C93.3392 92.2564 136.499 84.2345 172.499 117.735C208.5 151.236 205.5 194.737 205.5 194.737"
          stroke="#F4EADA"
        />
        <path d="M387 219.236L298 219.236" stroke="#F4EADA" />
        <path d="M582 219.023H465" stroke="#F4EADA" />
        <path
          d="M622 544.236C523.736 404.422 420 264.236 420 264.236"
          stroke="#F4EADA"
        />
        <path d="M622 544.236L206 264.236" stroke="#F4EADA" />
        <line
          x1="621.5"
          y1="544.236"
          x2="621.5"
          y2="264.236"
          stroke="#F4EADA"
        />
      </svg>

      <svg
        width="359"
        height="480"
        viewBox="0 0 359 480"
        className={s.mobile}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M90.9987 10.9498C90.9987 10.9498 150.05 -17.1804 172.312 48.5542C194.574 114.289 183 197.5 183 197.5"
          stroke="#F4EADA"
        />
        <path
          d="M88.6203 11.9903C88.6203 11.9903 184.045 -35.7776 250.158 67.6159C297.828 142.167 310 228.5 310 285"
          stroke="#F4EADA"
        />
        <path
          d="M92.4394 10.3407C92.4394 10.3407 130.047 -4.78945 114.899 30.1437C99.7511 65.0768 61.5 114 61.5 114"
          stroke="#F4EADA"
        />
        <path d="M67 155L135 214" stroke="#F4EADA" />
        <path d="M26 177L179.5 449.52" stroke="#F4EADA" />
        <path d="M163 259L179.277 449.223" stroke="#F4EADA" />
        <path d="M176 249L244 303" stroke="#F4EADA" />
        <line
          x1="263.387"
          y1="346.299"
          x2="179.532"
          y2="448.967"
          stroke="#F4EADA"
        />
      </svg>
    </div>
  );
};

LineMap.displayName = "LineMap";
